import { useServerExport } from './useServerExport';

/**
 * Enqueues the scenario-assumptions XLSX export for an EvaluationCalculation and tracks it to
 * completion over Pusher (`eval_calculator_<calculationId>` / `scenario_assumptions_export_complete`).
 *
 * A thin wrapper over {@link useServerExport} with no export-specific state, so it lives in
 * `lib/hooks` and is shared by two callers on two pages: the legacy AFCF toolbar and the new
 * `/eval3` Actions menu. The default download path is `downloadFromUrl` (same-tab, popup-safe;
 * see useServerExport) — the legacy AFCF caller uses it as-is. The new-page flow passes
 * `autoDownload: false` to download the URL itself, so it can surface an error toast when a
 * completion returns no download link.
 *
 * @param {object} options - Export configuration options.
 * @param {number | null} [options.calculationId] - The EvaluationCalculation id to export.
 * @param {boolean} [options.autoDownload] - Whether the hook auto-downloads (via `downloadFromUrl`)
 *   on completion (default true). Pass false to surface `exportStatus.downloadUrl` and download it yourself.
 * @return {object} The export status and control functions (from useServerExport).
 */
export const useScenarioAssumptionsExport = ({
  calculationId,
  autoDownload,
}: {
  calculationId?: number | null;
  autoDownload?: boolean;
}) => {
  // `?? null` maps undefined→null while keeping a real `0` id (truthiness would drop 0), matching
  // the eval3 `useCalcLifecycle` convention; a strict null check then gates the channel + fetch.
  const id = calculationId ?? null;
  return useServerExport({
    // Null before a calc is selected, so useServerExport skips the subscribe rather than binding
    // the literal `eval_calculator_null` channel.
    channel: id !== null ? `eval_calculator_${id}` : null,
    event: 'scenario_assumptions_export_complete',
    autoDownload,
    exportFn: async () => {
      if (id === null) throw new Error('No calculation selected');

      const csrfToken =
        document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute('content') || '';

      const response = await fetch(
        `/evaluations/calculations/${id}/downloads/scenario_assumptions_export`,
        {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
          },
        }
      );

      if (!response.ok) {
        const body = await response.json().catch(() => null);
        throw new Error(body?.error || `Export failed (${response.status})`);
      }

      const data = (await response.json()) as { export_id?: string };
      if (!data.export_id) {
        throw new Error('Export did not return an export_id');
      }
      return { exportId: data.export_id };
    },
  });
};
